import { createStore } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import { CheckRegionsResponse } from '@/services/check-regions'
import { CreateDiagnosisResponse } from '@/services/create-diagnosis'

import { FileToUpload } from '../../../hooks/mutations/use-create-diagnosis/manager/upload-queue'
import { DefaultPhysiologicalRegisterForm } from '../steps/physiological-register/hooks/use-physiological-register-form'
import { DefaultPlantingFailureFormData } from '../steps/planting-failure-info/hooks/use-planting-failure-form'
import { DefaultRegisterFormData } from '../steps/register/hooks/use-register-form'
import { DefaultSelectFilesFormData } from '../steps/select-files/hooks/use-select-files-form'

interface DiagnosisRegistrationState {
  registerData: DefaultRegisterFormData | null
  plantingFailureData: DefaultPlantingFailureFormData | null
  physiologicalRegisterData: DefaultPhysiologicalRegisterForm | null
  selectFilesData: DefaultSelectFilesFormData | null
  polygonsData: CheckRegionsResponse['matching'] | null

  diagnosisCreationData: CreateDiagnosisResponse | null
  filesToUpload: FileToUpload[] | null
  diagnosisConfirmed: boolean
  retryCount: number
}

interface DiagnosisRegistrationActions {
  setRegisterData: (data: DefaultRegisterFormData | null) => void
  setPlantingFailureData: (data: DefaultPlantingFailureFormData | null) => void
  setPhysiologicalRegisterData: (
    data: DefaultPhysiologicalRegisterForm | null,
  ) => void
  setSelectFilesData: (data: DefaultSelectFilesFormData | null) => void
  setPolygonsData: (data: DiagnosisRegistrationState['polygonsData']) => void

  setDiagnosisCreationData: (data: CreateDiagnosisResponse | null) => void
  setFilesToUpload: (data: DiagnosisRegistrationState['filesToUpload']) => void
  markFileAsDone: (index: number) => void
  markDiagnosisAsConfirmed: () => void
  setRetryCount: (count: number) => void

  reset: () => void
  resetUploads: () => void
}

export type DiagnosisRegistrationStoreData = DiagnosisRegistrationState &
  DiagnosisRegistrationActions

export type DiagnosisRegistrationStore = ReturnType<
  typeof createDiagnosisRegistrationStore
>

export function createDiagnosisRegistrationStore() {
  return createStore<DiagnosisRegistrationStoreData>()(
    immer((set) => ({
      registerData: null,
      plantingFailureData: null,
      physiologicalRegisterData: null,
      selectFilesData: null,
      polygonsData: null,
      diagnosisCreationData: null,
      filesToUpload: null,
      diagnosisConfirmed: false,
      retryCount: 0,

      setRegisterData(data) {
        set({ registerData: data })
      },
      setPlantingFailureData(data) {
        set({ plantingFailureData: data })
      },
      setPhysiologicalRegisterData(data) {
        set({ physiologicalRegisterData: data })
      },
      setSelectFilesData(data) {
        set({ selectFilesData: data })
      },
      setPolygonsData(data) {
        set({ polygonsData: data })
      },

      setDiagnosisCreationData(data) {
        set({ diagnosisCreationData: data })
      },
      setFilesToUpload(data) {
        set({ filesToUpload: data })
      },
      markFileAsDone(index) {
        set((state) => {
          if (!state.filesToUpload) return

          state.filesToUpload[index].done = true
        })
      },
      markDiagnosisAsConfirmed() {
        set({ diagnosisConfirmed: true })
      },
      setRetryCount(count) {
        set({ retryCount: count })
      },

      reset() {
        set({
          registerData: null,
          plantingFailureData: null,
          physiologicalRegisterData: null,
          selectFilesData: null,
          polygonsData: null,
          diagnosisCreationData: null,
          filesToUpload: null,
          diagnosisConfirmed: false,
          retryCount: 0,
        })
      },
      resetUploads() {
        set({
          filesToUpload: null,
          diagnosisConfirmed: false,
        })
      },
    })),
  )
}
